import type { ComponentProps, ReactNode } from "react"
import { useEffect, useMemo, useRef, useState } from "react"
import { Link } from "@tanstack/react-router"
import { motion } from "motion/react"
import {
	IconChevronRightOutline18 as ChevronRight,
	IconCircleWarningOutline18 as CircleWarning,
	IconMagnifierOutline18 as Magnifier,
	IconArrowRotateClockwiseOutline18 as Refresh,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"

import type {
	CustomDataTarget,
	SidebarSectionComponent,
} from "@productlane/api/src/browser"
import type { SidebarSection } from "@productlane/zero"
import { connectorReachesAgent } from "@productlane/lib/src/browser"
import { cn } from "@productlane/lib/src/cn"
import { IconButton, Loader, Tooltip } from "@productlane/ui"

import { ConnectorLogo } from "@/components/connector-logo"
import { api } from "@/lib/api"
import { useTrial } from "@/lib/useTrial"
import {
	SIDEBAR_GROUP_PADDING,
	SIDEBAR_TRAILING_ICON_PULL,
	SidebarGroupLabel,
} from "./shared/sidebar-group"
import { useMeasuredHeight } from "./shared/use-measured-height"
import {
	BaseComponent,
	isSpacingComponent,
} from "./sidebar-sections/base-component"
import {
	filterComponentsBySearch,
	sectionMatchesSearch,
} from "./sidebar-sections/search"
import { SidebarSectionProvider } from "./sidebar-sections/section-context"
import { useSidebarConnectors } from "./sidebar-sections/use-sidebar-connectors"
import { UnverifiedContactWarning } from "./unverified-contact-warning"

const COLLAPSE_STORAGE_PREFIX = "sidebar-section-open:"

function useSectionOpenState(sectionId: string) {
	const storageKey = `${COLLAPSE_STORAGE_PREFIX}${sectionId}`
	// Open by default; a stored value means the user explicitly toggled it.
	const [isOpen, setIsOpen] = useState(() => {
		try {
			const stored = window.localStorage.getItem(storageKey)
			if (stored !== null) return stored === "1"
		} catch {
			// localStorage unavailable
		}
		return true
	})

	const toggle = () => {
		setIsOpen((prev) => {
			const next = !prev
			try {
				window.localStorage.setItem(storageKey, next ? "1" : "0")
			} catch {
				// localStorage unavailable
			}
			return next
		})
	}

	return [isOpen, toggle] as const
}

type Props = ComponentProps<"div"> & {
	// The record the connectors read: the open thread, a contact, or a company.
	target: CustomDataTarget
}

export function CustomSidebarSections({ className, target, ...props }: Props) {
	const { sections: sidebarSections, settled } = useSidebarConnectors()
	const [isSearchOpen, setIsSearchOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState("")
	const searchInputRef = useRef<HTMLInputElement>(null)

	const query = searchQuery.trim().toLowerCase()
	const isContentSearch = query.length > 0

	useEffect(() => {
		if (isSearchOpen) {
			searchInputRef.current?.focus()
		}
	}, [isSearchOpen])

	// Same query key as each section row, so react-query dedupes the call.
	const contentQueries = api.useQueries((t) =>
		sidebarSections.map((section) =>
			t.customData.getSectionData(
				{ cardId: section.id, target },
				{
					enabled: isContentSearch,
					refetchOnMount: false,
					refetchOnWindowFocus: false,
				},
			),
		),
	)

	const filteredSections = useMemo(() => {
		if (!query) return sidebarSections
		return sidebarSections.filter((section, index) =>
			sectionMatchesSearch(
				section.title,
				contentQueries[index]?.data?.components,
				query,
			),
		)
	}, [sidebarSections, contentQueries, query])

	const closeSearch = () => {
		setIsSearchOpen(false)
		setSearchQuery("")
	}

	if (sidebarSections.length === 0) {
		if (!settled) return null
		return (
			<div className={cn("flex flex-col gap-1", className)} {...props}>
				<EmptyCustomDataSidebar />
			</div>
		)
	}

	return (
		<div
			className={cn(SIDEBAR_GROUP_PADDING, "flex flex-col gap-0.5", className)}
			{...props}
		>
			<div className="mb-2 flex h-7 items-center gap-2 px-2">
				{isSearchOpen ? (
					<div className="flex h-7 w-full items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 dark:border-gray-700/80 dark:bg-gray-800">
						<Magnifier
							strokeWidth={1.75}
							className="h-3.5 w-3.5 flex-none text-tertiary"
						/>
						<input
							ref={searchInputRef}
							name="custom-connector-search"
							placeholder="Filter connectors..."
							value={searchQuery}
							className="min-w-0 flex-1 bg-transparent text-xs text-primary outline-none placeholder:text-tertiary"
							onChange={(e) => setSearchQuery(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Escape") closeSearch()
							}}
						/>
						<IconButton
							Icon={XIcon}
							aria-label="Close connector search"
							variant="ghost-brightness"
							className="-mr-1.5 flex-none text-tertiary"
							iconClassName="h-3.5 w-3.5"
							onClick={closeSearch}
						/>
					</div>
				) : (
					<>
						<div className="min-w-0 flex-1 select-none truncate text-xs font-medium text-gray-500 dark:text-gray-450">
							Connectors
						</div>
						<Tooltip label="Search connectors">
							<IconButton
								Icon={Magnifier}
								aria-label="Search connectors"
								variant="ghost"
								className={cn(
									"flex-none text-tertiary",
									SIDEBAR_TRAILING_ICON_PULL,
								)}
								iconClassName="h-3.5 w-3.5"
								onClick={() => setIsSearchOpen(true)}
							/>
						</Tooltip>
					</>
				)}
			</div>

			{filteredSections.length > 0 ? (
				filteredSections.map((section) => (
					<CustomSidebarSection
						section={section}
						key={section.id}
						target={target}
						forceOpen={isContentSearch}
						forceFetch={isContentSearch}
						searchQuery={query}
					/>
				))
			) : (
				<div className="select-none px-2 py-2 text-xs text-tertiary">
					{isContentSearch && contentQueries.some((q) => q.isLoading)
						? "Searching…"
						: `No connectors match "${searchQuery.trim()}"`}
				</div>
			)}
		</div>
	)
}

interface CustomSidebarSectionProps {
	section: SidebarSection
	target: CustomDataTarget
	forceOpen?: boolean
	forceFetch?: boolean
	/** Lowercased; narrows the section down to the fields that match. */
	searchQuery?: string
}

function SectionHeader({
	section,
	isOpen,
	onClick,
	actions,
	warning,
}: {
	section: SidebarSection
	isOpen: boolean
	onClick: () => void
	actions?: ReactNode
	warning?: ReactNode
}) {
	return (
		<div className="flex h-7 items-center gap-1 rounded-md px-2 hover:bg-gray-150 dark:hover:bg-gray-800">
			<button
				type="button"
				onClick={onClick}
				className="flex h-7 min-w-0 flex-1 select-none items-center gap-1.5 text-left"
			>
				<ConnectorLogo url={section.url} logoUrl={section.logoUrl} />
				<span className="flex min-w-0 items-center gap-0.5">
					<span className="min-w-0 truncate text-xs font-medium text-secondary">
						{section.title}
					</span>
					<motion.span
						animate={{ rotate: isOpen ? 90 : 0 }}
						transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
						className="flex h-3.5 w-3.5 flex-none items-center justify-center text-tertiary"
					>
						<ChevronRight className="h-3 w-3 flex-none" />
					</motion.span>
				</span>
				{warning}
			</button>
			{actions ? (
				<div className="flex flex-none items-center">{actions}</div>
			) : null}
		</div>
	)
}

// Delay before firing the sidebar section HTTP request after landing on a
// thread. Sidebar data is non-critical - if the user is rapidly arrowing
// through threads we'd rather skip the request entirely than fire one per
// intermediate thread (each with its own network round-trip).
const SIDEBAR_REQUEST_DELAY_MS = 200

function CustomSidebarSection({
	section,
	target,
	forceOpen = false,
	forceFetch = false,
	searchQuery = "",
}: CustomSidebarSectionProps) {
	const [isOpenStored, toggleOpen] = useSectionOpenState(section.id)
	const isOpen = forceOpen || isOpenStored

	// Only allow the query to run once the target id has been stable for
	// SIDEBAR_REQUEST_DELAY_MS, and only when the section is expanded. Search
	// bypasses the delay so content is available to filter against.
	const [enabled, setEnabled] = useState(false)
	useEffect(() => {
		if (forceFetch) {
			setEnabled(true)
			return
		}
		if (!isOpen) {
			setEnabled(false)
			return
		}
		setEnabled(false)
		const t = setTimeout(() => setEnabled(true), SIDEBAR_REQUEST_DELAY_MS)
		return () => clearTimeout(t)
	}, [target.id, isOpen, forceFetch])

	const sectionQuery = api.customData.getSectionData.useQuery(
		{
			cardId: section.id,
			target,
		},
		{ refetchOnMount: false, refetchOnWindowFocus: false, enabled },
	)

	// An action can hand back a fresh render, so show that instead of refetching.
	const [overrideComponents, setOverrideComponents] = useState<
		SidebarSectionComponent[] | null
	>(null)
	useEffect(() => {
		setOverrideComponents(null)
	}, [target.id, section.id])

	const components = overrideComponents ?? sectionQuery.data?.components

	// A title match keeps the whole connector; otherwise only matching fields show.
	const visibleComponents = useMemo(() => {
		if (!components || !searchQuery) return components
		if (section.title.toLowerCase().includes(searchQuery)) return components
		return filterComponentsBySearch(components, searchQuery)
	}, [components, searchQuery, section.title])

	// Provider errors arrive as raw multi-line SDK text; flatten it for the row.
	const errorText = sectionQuery.data?.error?.replace(/\s+/g, " ").trim() ?? ""

	// An action result can hand back fresh components, but which actions the agent
	// may run comes from the connector's manifest, not from the render.
	const agentActions = useMemo(
		() => new Set(sectionQuery.data?.agentActions ?? []),
		[sectionQuery.data?.agentActions],
	)

	const refresh = () => {
		setOverrideComponents(null)
		void sectionQuery.refetch()
	}

	const [contentHeight, setContentEl] = useMeasuredHeight()

	return (
		<div>
			<SectionHeader
				section={section}
				isOpen={isOpen}
				onClick={toggleOpen}
				actions={
					// Slot stays mounted so the title never re-truncates on toggle.
					<div
						className={cn("flex", !isOpen && "pointer-events-none opacity-0")}
					>
						<Tooltip label="Refresh" enabled={isOpen}>
							<IconButton
								Icon={Refresh}
								variant="ghost-brightness"
								className={cn(
									"flex-none text-tertiary",
									SIDEBAR_TRAILING_ICON_PULL,
								)}
								disabled={sectionQuery.isFetching}
								tabIndex={isOpen ? 0 : -1}
								onClick={refresh}
								iconClassName="h-3.5 w-3.5"
							/>
						</Tooltip>
					</div>
				}
				warning={
					sectionQuery.data?.contactIsUnverified ? (
						<UnverifiedContactWarning
							agentEnabled={connectorReachesAgent(section)}
						/>
					) : null
				}
			/>

			<motion.div
				initial={false}
				animate={{
					height: isOpen ? contentHeight : 0,
					opacity: isOpen ? 1 : 0,
				}}
				transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
				style={{ overflow: "hidden" }}
			>
				<div ref={setContentEl} className="px-2 pb-2.5 pt-1.5">
					{sectionQuery.isFetching && !visibleComponents ? (
						<div className="flex h-6 select-none items-center gap-2">
							<Loader className="flex-none" />
							<span className="text-xs text-tertiary">
								Loading {section.title}...
							</span>
						</div>
					) : null}
					{!visibleComponents && sectionQuery.data?.error ? (
						<Tooltip
							label={<span className="block max-w-64">{errorText}</span>}
						>
							<div className="flex items-start gap-1.5 rounded-md bg-red-50 px-2 py-1.5 text-left ring-1 ring-inset ring-red-600/20 dark:bg-red-500/10 dark:ring-red-500/20">
								<CircleWarning
									strokeWidth={1.75}
									className="mt-px h-3.5 w-3.5 flex-none text-red-600 dark:text-red-400"
								/>
								<span className="line-clamp-2 min-w-0 text-xs text-red-700 dark:text-red-400">
									{errorText}
								</span>
							</div>
						</Tooltip>
					) : null}
					{visibleComponents ? (
						<SidebarSectionProvider
							value={{
								sectionId: section.id,
								target,
								agentActions,
								onActionResult: (next) => {
									if (next) setOverrideComponents(next)
									else refresh()
								},
							}}
						>
							{visibleComponents.map((component, i) => {
								const prev = visibleComponents[i - 1]
								const gapBefore = Boolean(
									prev &&
									!isSpacingComponent(prev) &&
									!isSpacingComponent(component),
								)
								return (
									<BaseComponent
										component={component}
										key={i}
										gapBefore={gapBefore}
									/>
								)
							})}
						</SidebarSectionProvider>
					) : null}
				</div>
			</motion.div>
		</div>
	)
}

function EmptyCustomDataSidebar() {
	const { isTrial } = useTrial()

	if (!isTrial) return

	return (
		<div className={cn(SIDEBAR_GROUP_PADDING, "flex flex-col")}>
			<SidebarGroupLabel>Connectors</SidebarGroupLabel>

			<div className="px-2 text-xs font-normal">
				<span className="select-none text-tertiary">
					Show live customer data here, like revenue from Stripe or active users
					in your database.
				</span>{" "}
				<Link to="/settings/custom-connectors">Learn more</Link>
			</div>
		</div>
	)
}
